import { useEffect, useState, useContext } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import MUICard from "./../../../Component/UI/MUICard/MUICard";
import classes from "./History.module.css";
import HistoryItem from "./HistoryItem";
import LoadingSkeleton from "./LoadingSkeleton";
import EmptyView from "./../../../Component/EmptyView/EmptyView";
import { AuthContext } from "../../../context/auth-context";
import useHistory from "./useHistory";
import MUIButton from "./../../../Component/UI/MUIButton/MUIButton";
import ErrorView from "./../../../Component/ErrorView/ErrorView";
import ErrorMessage from "./../../../Component/ErrorMessage/ErrorMessage";
import api from "../../../BrandOnBoarding/api";

const PAGE_LIMIT = 5;
const DEMO_EMAIL = "demo@yopmail.com";

export default function History({
  filter = "brand",
  updateStatesFromHistory,
  isDemoAvailable = false,
  demoMode = false,
  onDemoModeChange,
}) {
  const [filterOption, setFilterOption] = useState(filter);
  const { authUserEmail } = useContext(AuthContext);
  const [page, setPage] = useState(1);
  const [demoHistory, setDemoHistory] = useState([]);

  const {
    history,
    loading,
    getHistory,
    hasReachedEnd,
    deleteHistory,
    updateHistory,
    error,
  } = useHistory(authUserEmail, filterOption);

  useEffect(() => {
    setFilterOption(filter);
    setPage(1);
  }, [filter]);

  useEffect(() => {
    getHistory(page, PAGE_LIMIT);
  }, [page, filterOption]);

  useEffect(() => {
    if (!demoMode) {
      setDemoHistory([]);
      return;
    }
    async function fetchDemoHistory() {
      try {
        const response = await api.get(
          `/users/content-map-history?user_email=${encodeURIComponent(DEMO_EMAIL)}&page=1&limit=50&type=${filterOption}&is_purchase_journey=false&is_conversations=false`
        );
        if (response.status === 200) {
          const data = response.data.data.map((item) => ({
            ...item,
            market: item?.json_data?.market,
          }));
          setDemoHistory(data);
        }
      } catch (e) {}
    }
    fetchDemoHistory();
  }, [demoMode, filterOption]);

  const mergedHistory = demoMode ? [...history, ...demoHistory.slice(0, 3)] : history;

  function deleteHistoryItemHandler(id) {
    deleteHistory(id);
    if (history.length === 1) {
      setPage((page) => page + 1);
    }
  }

  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      <div className={classes.header} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className={classes.title}>History</span>
        {isDemoAvailable && (
          <Tooltip title="Enable to view demo data" arrow>
            <label
              style={{
                cursor: "pointer",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                userSelect: "none",
                whiteSpace: "nowrap",
              }}
            >
              <input
                type="checkbox"
                checked={demoMode}
                onChange={(e) => onDemoModeChange && onDemoModeChange(e.target.checked)}
                style={{ cursor: "pointer", width: "12px", height: "12px", marginRight: 2 }}
              />
              <span style={{ lineHeight: "12px", fontSize: "12px" }}>Demo</span>
            </label>
          </Tooltip>
        )}
      </div>
      {mergedHistory.length > 0 && (
        <div className={classes.container}>
          {mergedHistory.map((historyItem) => (
            <HistoryItem
              key={historyItem.id}
              item={historyItem}
              onDelete={deleteHistoryItemHandler}
              onUpdate={(id, name) => updateHistory(id, name)}
              updateStatesFromHistory={updateStatesFromHistory}
              isDemoMode={demoMode}
            />
          ))}
          <div className={classes.load_more}>
            {error && mergedHistory.length > 0 && (
              <div className={classes.error_message}>
                <ErrorMessage message={error} />
              </div>
            )}
            {!loading && !hasReachedEnd && (
              <MUIButton
                className={classes.load_btn}
                variant="text"
                onClick={() => setPage((page) => page + 1)}
              >
                Load More
              </MUIButton>
            )}
            {loading && <CircularProgress size={22} />}
          </div>
        </div>
      )}
      {loading && mergedHistory.length === 0 && <LoadingSkeleton />}
      {!loading && !error && mergedHistory.length === 0 && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "70%" }}
        >
          <EmptyView message={"Nothing here."} />
        </div>
      )}
      {error && mergedHistory.length === 0 && (
        <div className={classes.error}>
          <ErrorView title="Error" message={error} />
        </div>
      )}
    </MUICard>
  );
}
